import type { CommandKind, CommandPayloads } from '@didto/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { runCommand } from '@/lib/api';
import type { CommandResult } from '@/lib/api';
import { PAUSED_REASON, currentConnectivity } from '@/lib/connectivity';
import { toAppError } from '@/lib/errors';
import { useToast } from './toast';

/** Discriminated union of every command with its payload, so `mutateAsync` is type-checked. */
export type CommandArgs = {
  [K in CommandKind]: {
    kind: K;
    payload: CommandPayloads[K];
    commandId?: string;
    locationId?: string;
  };
}[CommandKind];

/**
 * Run a command with the standard UX: invalidate the given query keys on success, toast the
 * server's error message on failure. The command id is generated per call (a retry is a new
 * command); components that need retry-safety keep the id themselves.
 */
export function useCommand(invalidate: string[][] = []) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation<CommandResult, unknown, CommandArgs>({
    mutationFn: (args) => {
      // ADR-037: nothing is sent while the server cannot be reached
      if (currentConnectivity() !== 'online')
        return Promise.reject(new Error(`network: ${PAUSED_REASON}`));
      return runCommand(args.kind, args.payload as never, {
        ...(args.commandId ? { commandId: args.commandId } : {}),
        ...(args.locationId ? { locationId: args.locationId } : {}),
      });
    },
    onSuccess: async () => {
      await Promise.all([...invalidate, ['me']].map((k) => qc.invalidateQueries({ queryKey: k })));
    },
    onError: (e) => {
      toast(toAppError(e).message, 'danger');
    },
  });
}
